/** Client-side session/idle policy. Backend token TTLs are authoritative; these
 * govern the UX idle-timeout warning + auto sign-out. */
export const sessionConfig = {
  /** Total idle time before auto sign-out (ms), across ALL open tabs. */
  idleTimeoutMs: 30 * 60_000,      // 30 minutes
  /** Idle limit when the user chose "Remember me" at sign-in (ms). */
  rememberedIdleTimeoutMs: 12 * 60 * 60_000, // 12 hours
  /** How long before timeout to show the "still there?" warning (ms). */
  warnBeforeMs: 2 * 60_000,        // warn at 2 minutes remaining
  /** Silently refresh the access token on this interval while active (ms). */
  keepAliveMs: 10 * 60_000,        // 10 minutes
};

export const REMEMBER_KEY = 'nh.remember';
export const LAST_EMAIL_KEY = 'nh.lastEmail';
/** Shared (cross-tab) timestamp of the last user activity in any tab. */
export const LAST_ACTIVITY_KEY = 'nh.lastActivity';
/** Cross-tab sign-out notice: { reason, at, idleMinutes? }. */
export const SIGNED_OUT_KEY = 'nh.signedOut';

export type SignOutReason = 'idle' | 'logout';

/** Idle limit for this browser: 12h with "Remember me", otherwise 30 minutes. */
export function idleTimeoutMs(): number {
  try {
    return localStorage.getItem(REMEMBER_KEY) === '1' ? sessionConfig.rememberedIdleTimeoutMs : sessionConfig.idleTimeoutMs;
  } catch {
    return sessionConfig.idleTimeoutMs;
  }
}

export function recordSignOut(reason: SignOutReason, idleMs?: number): void {
  try {
    localStorage.setItem(SIGNED_OUT_KEY, JSON.stringify({ reason, at: Date.now(), idleMinutes: idleMs ? Math.round(idleMs / 60_000) : undefined }));
  } catch { /* storage unavailable */ }
}

export function readSignOut(): { reason: SignOutReason; at: number; idleMinutes?: number } | null {
  try {
    const raw = localStorage.getItem(SIGNED_OUT_KEY);
    return raw ? (JSON.parse(raw) as { reason: SignOutReason; at: number; idleMinutes?: number }) : null;
  } catch {
    return null;
  }
}

export function clearSignOut(): void {
  try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { /* ignore */ }
}
